import { Controller } from "@hotwired/stimulus"

// Mirrors the crosshair + tooltip across every Highcharts chart inside this
// element (index price chart above the market breadth chart).
//
// Two deviations from naive syncing, both load-bearing:
// - Highcharts tracks a single global hover chart; hovering a point on chart B
//   fires a pointer reset on chart A that immediately hides what we just drew.
//   While the cursor is over a chart, each chart's pointer.reset is swapped
//   for a no-op (scoped to these chart instances — not the prototype override
//   the Highcharts sync demo uses); the original is restored, and the
//   tooltips/crosshairs cleared, whenever the cursor is over neither chart.
// - The charts' plot areas start at different x offsets (their y-axis labels
//   differ in width), so the same pixel maps to different dates. The hovered
//   chart resolves the point by pixel; the others match it by x value.
export default class extends Controller {
  connect() {
    this.onMove = this.handleMove.bind(this)
    this.onLeave = this.clearAll.bind(this)
    ;["mousemove", "touchmove", "touchstart"].forEach(type =>
      this.element.addEventListener(type, this.onMove)
    )
    this.element.addEventListener("mouseleave", this.onLeave)
  }

  disconnect() {
    ;["mousemove", "touchmove", "touchstart"].forEach(type =>
      this.element.removeEventListener(type, this.onMove)
    )
    this.element.removeEventListener("mouseleave", this.onLeave)
    this.restoreResets()
    this.chartCache = null
  }

  // Highcharts.charts holds every chart the app has ever created (destroyed
  // slots stay behind as undefined), so resolve the in-scope charts once and
  // reuse the list until one of them is destroyed or replaced.
  charts() {
    if (this.chartCache && this.chartCache.every(chart => chart.renderer)) {
      return this.chartCache
    }
    this.restoreResets()
    if (typeof Highcharts === "undefined") return []
    this.chartCache = Highcharts.charts.filter(chart =>
      chart && chart.renderer && chart.renderTo && this.element.contains(chart.renderTo)
    )
    return this.chartCache
  }

  handleMove(e) {
    const charts = this.charts()
    if (charts.length === 0) return

    // In the padding between the two bordered chart panels there is nothing
    // to hover — clear both charts rather than pinning a stale crosshair.
    const source = charts.find(chart => chart.renderTo.contains(e.target))
    if (!source || !source.series.length) {
      this.clearAll()
      return
    }

    this.disableResets(charts)

    // First series is always the mouse-tracked line on both charts (the
    // breadth chart's extra zone series have mouse tracking disabled).
    const event = source.pointer.normalize(e)
    const sourcePoint = source.series[0].searchPoint(event, true)
    if (!sourcePoint) return

    charts.forEach(chart => {
      const point = chart === source ? sourcePoint : this.nearestPointByX(chart, sourcePoint.x)
      if (!point) return
      point.onMouseOver()
      chart.xAxis[0].drawCrosshair(null, point)
    })
  }

  nearestPointByX(chart, x) {
    const points = (chart.series[0] && chart.series[0].points) || []
    let best = null
    let bestDistance = Infinity
    points.forEach(point => {
      const distance = Math.abs(point.x - x)
      if (distance < bestDistance) {
        best = point
        bestDistance = distance
      }
    })
    return best
  }

  disableResets(charts) {
    charts.forEach(chart => {
      if (chart.pointer && !chart.pointer.chartSyncOriginalReset) {
        chart.pointer.chartSyncOriginalReset = chart.pointer.reset
        chart.pointer.reset = () => undefined
      }
    })
  }

  restoreResets() {
    ;(this.chartCache || []).forEach(chart => {
      if (chart.pointer && chart.pointer.chartSyncOriginalReset) {
        chart.pointer.reset = chart.pointer.chartSyncOriginalReset
        delete chart.pointer.chartSyncOriginalReset
      }
    })
  }

  clearAll() {
    this.charts().forEach(chart => {
      if (chart.hoverPoint) chart.hoverPoint.setState("")
      if (chart.tooltip) chart.tooltip.hide(0)
      if (chart.xAxis[0]) chart.xAxis[0].hideCrosshair()
    })
    this.restoreResets()
  }
}
